import { useCallback, useRef, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { SUPPORTED_FILE_TYPES } from '@dreamai/types';
import { v4 as uuidv4 } from 'uuid';
import api from '../../../api';
import { FileUploadState } from './types';

/**
 * Custom hook that handles file upload logic including validation and S3 upload
 */
export const useFileUpload = (
  onFileUpload: (fileState: FileUploadState) => void,
) => {
  const { getAccessTokenSilently } = useAuth0();
  const previewUrlsRef = useRef<string[]>([]);

  // Cleanup function to revoke all preview URLs
  const cleanupPreviews = useCallback(() => {
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    previewUrlsRef.current = [];
  }, []);

  // Cleanup preview URLs when component unmounts
  useEffect(() => {
    return () => {
      cleanupPreviews();
    };
  }, [cleanupPreviews]);

  const processFile = useCallback(
    async (file: File) => {
      // Validate file type
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (
        !fileExtension ||
        !SUPPORTED_FILE_TYPES.includes(`.${fileExtension}`)
      ) {
        onFileUpload({
          id: uuidv4(),
          name: file.name,
          size: file.size,
          type: file.type,
          status: 'error',
          error: `Unsupported file type. Supported types: ${SUPPORTED_FILE_TYPES.join(', ')}`,
        });
        return;
      }

      // Validate file size (10MB max)
      const MAX_FILE_SIZE = 10 * 1024 * 1024;
      if (file.size > MAX_FILE_SIZE) {
        onFileUpload({
          id: uuidv4(),
          name: file.name,
          size: file.size,
          type: file.type,
          status: 'error',
          error: 'File size exceeds 10MB limit',
        });
        return;
      }

      // Create preview URL for images
      let previewUrl: string | undefined;
      if (file.type.startsWith('image/')) {
        previewUrl = URL.createObjectURL(file);
        previewUrlsRef.current.push(previewUrl);
      }

      // Create file upload state
      const fileId = uuidv4();
      const fileState: FileUploadState = {
        id: fileId,
        name: file.name,
        size: file.size,
        type: file.type,
        status: 'uploading',
        previewUrl,
      };

      // Notify parent component
      onFileUpload(fileState);

      try {
        // Get access token from Auth0
        const token = await getAccessTokenSilently();

        // Get presigned URL from backend
        const { data } = await api.post(
          '/agent-files/presigned-upload',
          { fileExtension },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        // Create FormData for S3 upload
        const formData = new FormData();
        Object.entries(data.fields).forEach(([key, value]) => {
          formData.append(key, value as string);
        });
        formData.append('file', file);

        // Upload to S3
        const xhr = new XMLHttpRequest();
        xhr.open('POST', data.url);

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            onFileUpload({
              ...fileState,
              status: 'success',
              url: data.fileUrl,
            });
          } else {
            onFileUpload({
              ...fileState,
              status: 'error',
              error: `Upload failed with status ${xhr.status}`,
            });
          }
        };

        xhr.onerror = () => {
          onFileUpload({
            ...fileState,
            status: 'error',
            error: 'Network error occurred during upload',
          });
        };

        xhr.send(formData);
      } catch (error) {
        onFileUpload({
          ...fileState,
          status: 'error',
          error: 'Failed to get upload URL',
        });
      }
    },
    [onFileUpload, getAccessTokenSilently],
  );

  return { processFile, cleanupPreviews };
};
